const { dateOnlyKey, parseLocalDate } = require('./dates');

function taskListKey(task) {
  if (!task) return '';
  if (task.task_id) {
    const due = dateOnlyKey(task.due_date) || 'no-date';
    return `${task.task_id}-${due}`;
  }
  return `custom-${task.id}`;
}

function preferTask(candidate, existing, preferId) {
  if (preferId && candidate.id === preferId && existing.id !== preferId) return true;
  if (preferId && existing.id === preferId && candidate.id !== preferId) return false;
  if (!candidate.completed && existing.completed) return true;
  return false;
}

function dedupeTasks(tasks, preferId) {
  const unique = [];
  const indexByKey = new Map();
  (tasks || []).forEach((task) => {
    if (!task || !task.id) return;
    const key = taskListKey(task);
    if (!indexByKey.has(key)) {
      indexByKey.set(key, unique.length);
      unique.push(task);
      return;
    }
    const index = indexByKey.get(key);
    if (preferTask(task, unique[index], preferId)) unique[index] = task;
  });
  return unique;
}

function applyTaskCompletion(tasks, change = {}) {
  const taskId = change.taskId;
  const completed = Boolean(change.completed);
  const removeIds = new Set((change.removeIds || []).filter(Boolean));
  const next = (tasks || [])
    .filter((task) => task && !removeIds.has(task.id))
    .map((task) => {
      if (!taskId || task.id !== taskId) return task;
      return Object.assign({}, task, {
        completed,
        completed_at: completed ? (change.completedAt || task.completed_at || null) : null,
      });
    });
  (change.addTasks || []).forEach((task) => {
    if (task && task.id && !next.some((row) => row.id === task.id)) next.push(task);
  });
  return dedupeTasks(next, taskId);
}

function mergeLoadedTasks(previous, fetched, change) {
  const rows = Array.isArray(fetched) ? fetched.slice() : [];
  if (!change || !change.taskId) return dedupeTasks(rows);
  const local = (previous || []).find((task) => task && task.id === change.taskId);
  if (!rows.some((task) => task && task.id === change.taskId)) {
    if (local) rows.push(local);
  }
  const merged = applyTaskCompletion(rows, change);
  const localName = local && String(local.task_name || '').trim();
  if (!localName) return merged;
  return merged.map((task) => (
    task && task.id === change.taskId
      ? Object.assign({}, task, { task_name: localName })
      : task
  ));
}

function pendingTasks(tasks) {
  return (tasks || []).filter((task) => reminderIncludesTask(task));
}

function reminderIncludesTask(task) {
  return Boolean(task && !task.completed && !task.skipped_at);
}

/** Hidden through the day before snoozed_until. Visible again on that date. */
function isTaskSnoozed(task, now) {
  if (!task || task.completed) return false;
  const until = dateOnlyKey(task.snoozed_until);
  if (!until) return false;
  const today = dateOnlyKey(now instanceof Date ? now : new Date(now || Date.now()));
  return Boolean(today) && until > today;
}

function completedTaskRows(tasks) {
  return (tasks || []).filter((task) => task && task.completed);
}

function calendarTaskGroups(tasks, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date();
  const today = parseLocalDate(now) || new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);
  const showCompleted = Boolean(options.showCompleted);
  const pending = pendingTasks(tasks, now).filter((task) => !isTaskSnoozed(task, now));
  const completed = completedTaskRows(tasks);
  const due = (task) => parseLocalDate(task.due_date);
  const byDue = (a, b) => (due(a) || 0) - (due(b) || 0);
  const ordered = (rows) => {
    const dated = rows.filter((task) => task.due_date).sort(byDue);
    const undated = rows.filter((task) => !task.due_date);
    return dated.concat(undated);
  };
  return {
    overdue: pending.filter((task) => {
      const date = due(task);
      return date && date < today;
    }).sort(byDue),
    thisWeek: pending.filter((task) => {
      const date = due(task);
      return date && date >= today && date < nextWeek;
    }).sort(byDue),
    completed: showCompleted ? ordered(completed) : [],
    dots: showCompleted ? pending.concat(completed) : pending,
    list: showCompleted ? ordered(pending.concat(completed)) : ordered(pending),
  };
}

function calendarDayIndicator(count) {
  const total = Number(count) || 0;
  if (total <= 0) return null;
  return { dot: true, count: total > 1 ? total : null };
}

function visibleTaskList(tasks, options = {}) {
  const groups = calendarTaskGroups(tasks, options);
  if (options.fullYear) return { tasks: groups.list, hidden: 0 };
  const now = options.now instanceof Date ? options.now : new Date();
  const today = parseLocalDate(now) || new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  end.setDate(end.getDate() + (Number(options.withinDays) || 60));
  const shown = groups.list.filter((task) => {
    const date = parseLocalDate(task && task.due_date);
    if (!date) return true;
    return date < end;
  });
  return { tasks: shown, hidden: groups.list.length - shown.length };
}

module.exports = {
  taskListKey,
  dedupeTasks,
  applyTaskCompletion,
  mergeLoadedTasks,
  pendingTasks,
  reminderIncludesTask,
  isTaskSnoozed,
  calendarTaskGroups,
  calendarDayIndicator,
  visibleTaskList,
};
